import { useState, useRef, useCallback, lazy, Suspense, type ReactNode } from 'react'
import { AuthProvider, useAuth } from '@/lib/auth'
import { NavProvider, type SectionId } from '@/lib/nav'
import AuthPage from '@/pages/Auth'
import Accueil from '@/pages/Accueil'
import BandeauRetourAbonnement from '@/components/BandeauRetourAbonnement'
import MurAbonnement from '@/components/MurAbonnement'
import UpsellModuleCosmetique from '@/components/UpsellModuleCosmetique'
import ErrorBoundary from '@/components/ErrorBoundary'
import BanniereMaj from '@/components/BanniereMaj'
import { accesActif } from '@shared/calculs/abonnement'
import { moduleOuvert } from '@shared/constants'
import { brouillonDepuisFiche } from '@/pages/calculateur/versFiche'
import type { BrouillonFiche } from '@/pages/calculateur/versFiche'
import type { BrouillonFicheCosmetique } from '@/pages/formulateur/versFicheCosmetique'
import { brouillonDepuisFicheCosmetique } from '@/pages/formulateur/versFicheCosmetique'
import type { FormuleInitiale } from '@/pages/Calculateur'

// Pages chargées À LA DEMANDE (code-splitting) : chacune n'est téléchargée qu'à sa première
// ouverture, ce qui allège le tout premier chargement de l'app et sort les grosses dépendances
// (génération PDF Factur-X côté Factures, scan code-barres côté Devis) du bundle initial. Les
// écrans d'entrée (Auth, Accueil) et les composants de structure restent immédiats pour un
// démarrage instantané.
const Calculateur = lazy(() => import('@/pages/Calculateur'))
const Formulateur = lazy(() => import('@/pages/Formulateur'))
const MesIngredients = lazy(() => import('@/pages/MesIngredients'))
const MesFormules = lazy(() => import('@/pages/MesFormules'))
const MesArticles = lazy(() => import('@/pages/MesArticles'))
const MesStock = lazy(() => import('@/pages/MesStock'))
const MesClients = lazy(() => import('@/pages/MesClients'))
const MesDevis = lazy(() => import('@/pages/MesDevis'))
const MesFactures = lazy(() => import('@/pages/MesFactures'))
const MesDepots = lazy(() => import('@/pages/MesDepots'))
const RecettesAchats = lazy(() => import('@/pages/RecettesAchats'))
const Reglages = lazy(() => import('@/pages/Reglages'))
const ComptesClientes = lazy(() => import('@/pages/admin/ComptesClientes'))
const Fiches = lazy(() => import('@/pages/Fiches'))
const FicheForm = lazy(() => import('@/pages/fiche/FicheForm'))
const FicheDetail = lazy(() => import('@/pages/fiche/FicheDetail'))
const FicheImpression = lazy(() => import('@/pages/fiche/FicheImpression'))
const FichesCosmetique = lazy(() => import('@/pages/FichesCosmetique'))
const FicheCosmetiqueForm = lazy(() => import('@/pages/fiche-cosmetique/FicheCosmetiqueForm'))
const FicheCosmetiqueDetail = lazy(() => import('@/pages/fiche-cosmetique/FicheCosmetiqueDetail'))
const FicheCosmetiqueImpression = lazy(() => import('@/pages/fiche-cosmetique/FicheCosmetiqueImpression'))

// Écran d'attente affiché pendant le chargement (statut auth en cours, ou page à la demande).
function EcranChargement() {
  return (
    <div className="min-h-screen bg-bdspBg flex items-center justify-center">
      <p className="text-stone-500">Chargement…</p>
    </div>
  )
}

// Écran courant : une section du menu, ou un sous-écran contextuel des fiches
// (savon ou cosmétique).
type Page =
  | SectionId
  | 'fiche-nouvelle'
  | 'fiche-detail'
  | 'fiche-impression'
  | 'fiche-cosmetique-nouvelle'
  | 'fiche-cosmetique-detail'
  | 'fiche-cosmetique-impression'

// Section à mettre en évidence dans le menu pour un écran donné (les sous-écrans
// de fiche restent rattachés à leur section « Lots »).
function sectionDeLaPage(page: Page): SectionId {
  if (page === 'fiche-nouvelle' || page === 'fiche-detail' || page === 'fiche-impression')
    return 'fiches'
  if (
    page === 'fiche-cosmetique-nouvelle' ||
    page === 'fiche-cosmetique-detail' ||
    page === 'fiche-cosmetique-impression'
  )
    return 'fiches-cosmetique'
  return page
}

function Shell() {
  const { status, user } = useAuth()
  const [page, setPage] = useState<Page>('accueil')
  const [brouillon, setBrouillon] = useState<BrouillonFiche | null>(null)
  const [ficheId, setFicheId] = useState<string | null>(null)
  // Lot cosmétique courant (état SÉPARÉ du savon : les deux écrans ne se mélangent jamais).
  const [ficheCosmetiqueId, setFicheCosmetiqueId] = useState<string | null>(null)
  // Brouillon cosmétique issu du pont « Fabriquer » (formule -> fiche pré-remplie). null = saisie manuelle.
  const [brouillonCosmetique, setBrouillonCosmetique] = useState<BrouillonFicheCosmetique | null>(null)
  // Brouillon de facture à ouvrir directement en édition (après conversion d'un devis).
  const [factureInitiale, setFactureInitiale] = useState<string | null>(null)
  // Formule à recharger dans le calculateur (depuis « Mes formules »), sinon null.
  const [formuleACharger, setFormuleACharger] = useState<FormuleInitiale | null>(null)

  // Garde de navigation : un écran (le calculateur) peut enregistrer une fonction
  // qui renvoie false pour ANNULER un départ (ex. modifications non enregistrées).
  const gardeNavigationRef = useRef<(() => boolean) | null>(null)
  const definirGardeNavigation = useCallback((garde: (() => boolean) | null) => {
    gardeNavigationRef.current = garde
  }, [])

  // Va vers une section du menu. Le calculateur s'ouvre vierge (on ne recharge une
  // formule que via « Mes formules » -> Charger). Respecte le garde de navigation.
  const naviguer = (section: SectionId) => {
    if (gardeNavigationRef.current && !gardeNavigationRef.current()) return
    if (section === 'calculateur') setFormuleACharger(null)
    setPage(section)
  }

  if (status === 'loading') {
    return <EcranChargement />
  }

  if (status !== 'authenticated') return <AuthPage />

  // Mur d'accès (brique S3) : si l'accès n'est plus actif (essai terminé / abonnement à
  // renouveler), on ne laisse ouvert que Réglages (pour s'abonner). La sécurité réelle est
  // côté serveur (middlewareAcces) ; ceci est l'équivalent visuel.
  if (user && !accesActif(user) && page !== 'reglages') {
    return (
      <NavProvider
        value={{ sectionCourante: sectionDeLaPage(page), naviguer, definirGardeNavigation }}
      >
        <BandeauRetourAbonnement />
        <MurAbonnement onSabonner={() => naviguer('reglages')} />
      </NavProvider>
    )
  }

  // Possession du module cosmétique : garde visuelle des écrans cosmétiques (le verrou réel
  // est côté serveur, exigeModule → 401/403). En temps normal l'entrée de menu est masquée.
  const aCosmetique = moduleOuvert(user?.modules, 'cosmetique')

  let contenu: ReactNode
  if (page === 'calculateur')
    contenu = (
      <Calculateur
        formuleInitiale={formuleACharger}
        onRetour={() => naviguer('accueil')}
        onEnregistrerFiche={(b) => {
          setBrouillon(b)
          setPage('fiche-nouvelle')
        }}
      />
    )
  else if (page === 'formulateur')
    contenu = moduleOuvert(user?.modules, 'cosmetique') ? (
      <Formulateur
        onRetour={() => naviguer('accueil')}
        onFabriquer={(b) => {
          setBrouillonCosmetique(b)
          setPage('fiche-cosmetique-nouvelle')
        }}
      />
    ) : (
      <UpsellModuleCosmetique
        onSabonner={() => naviguer('reglages')}
        onRetour={() => naviguer('accueil')}
      />
    )
  else if (page === 'ingredients')
    contenu = <MesIngredients onRetour={() => setPage('accueil')} />
  else if (page === 'formules')
    contenu = (
      <MesFormules
        onRetour={() => setPage('accueil')}
        onCharger={(f) => {
          setFormuleACharger(f)
          setPage('calculateur')
        }}
      />
    )
  else if (page === 'articles')
    contenu = <MesArticles onRetour={() => setPage('accueil')} />
  else if (page === 'stock')
    contenu = <MesStock onRetour={() => setPage('accueil')} />
  else if (page === 'clients')
    contenu = <MesClients onRetour={() => setPage('accueil')} />
  else if (page === 'devis')
    contenu = (
      <MesDevis
        onRetour={() => setPage('accueil')}
        onConvertiEnFacture={(factureId) => {
          setFactureInitiale(factureId)
          setPage('factures')
        }}
      />
    )
  else if (page === 'factures')
    contenu = (
      <MesFactures
        onRetour={() => setPage('accueil')}
        factureInitialeId={factureInitiale}
        onInitialeConsommee={() => setFactureInitiale(null)}
      />
    )
  else if (page === 'depots')
    contenu = (
      <MesDepots
        onRetour={() => setPage('accueil')}
        onFactureCreee={(factureId) => {
          setFactureInitiale(factureId)
          setPage('factures')
        }}
      />
    )
  else if (page === 'comptabilite')
    contenu = <RecettesAchats onRetour={() => setPage('accueil')} />
  else if (page === 'reglages')
    contenu = <Reglages onRetour={() => setPage('accueil')} />
  else if (page === 'comptes')
    contenu = <ComptesClientes onRetour={() => setPage('accueil')} />
  else if (page === 'fiches')
    contenu = (
      <Fiches
        onRetour={() => setPage('accueil')}
        onOuvrir={(id) => {
          setFicheId(id)
          setPage('fiche-detail')
        }}
        onOuvrirCosmetique={(id) => {
          setFicheCosmetiqueId(id)
          setPage('fiche-cosmetique-detail')
        }}
      />
    )
  else if (page === 'fiche-nouvelle' && brouillon)
    contenu = (
      <FicheForm
        brouillon={brouillon}
        onRetour={() => setPage('calculateur')}
        onEnregistre={(id) => {
          setFicheId(id)
          setPage('fiche-detail')
        }}
      />
    )
  else if (page === 'fiche-detail' && ficheId)
    contenu = (
      <FicheDetail
        id={ficheId}
        onRetour={() => setPage('fiches')}
        onImprimer={() => setPage('fiche-impression')}
        onDupliquer={(fiche) => {
          setBrouillon(brouillonDepuisFiche(fiche))
          setPage('fiche-nouvelle')
        }}
      />
    )
  else if (page === 'fiche-impression' && ficheId)
    contenu = <FicheImpression id={ficheId} onRetour={() => setPage('fiche-detail')} />
  else if (page === 'fiches-cosmetique')
    contenu = aCosmetique ? (
      <FichesCosmetique
        onRetour={() => setPage('accueil')}
        onOuvrir={(id) => {
          setFicheCosmetiqueId(id)
          setPage('fiche-cosmetique-detail')
        }}
        onNouveau={() => {
          setBrouillonCosmetique(null)
          setPage('fiche-cosmetique-nouvelle')
        }}
      />
    ) : (
      <UpsellModuleCosmetique
        titre="⚗️ Lots cosmétiques"
        description="Les lots de fabrication cosmétiques font partie du module « Cosmétique » (fiches de fabrication, traçabilité réglementaire et déstockage automatique à la libération). Il n'est pas encore activé sur ton compte."
        onSabonner={() => naviguer('reglages')}
        onRetour={() => naviguer('accueil')}
      />
    )
  else if (page === 'fiche-cosmetique-nouvelle')
    contenu = aCosmetique ? (
      <FicheCosmetiqueForm
        brouillon={brouillonCosmetique ?? undefined}
        onRetour={() => setPage(brouillonCosmetique ? 'formulateur' : 'fiches-cosmetique')}
        onCree={(id) => {
          setBrouillonCosmetique(null)
          setFicheCosmetiqueId(id)
          setPage('fiche-cosmetique-detail')
        }}
      />
    ) : (
      <UpsellModuleCosmetique
        titre="⚗️ Lots cosmétiques"
        description="Les lots de fabrication cosmétiques font partie du module « Cosmétique ». Il n'est pas encore activé sur ton compte."
        onSabonner={() => naviguer('reglages')}
        onRetour={() => naviguer('accueil')}
      />
    )
  else if (page === 'fiche-cosmetique-detail' && ficheCosmetiqueId)
    contenu = aCosmetique ? (
      <FicheCosmetiqueDetail
        id={ficheCosmetiqueId}
        onRetour={() => setPage('fiches-cosmetique')}
        onImprimer={() => setPage('fiche-cosmetique-impression')}
        onDupliquer={(fiche) => {
          setBrouillonCosmetique(brouillonDepuisFicheCosmetique(fiche))
          setPage('fiche-cosmetique-nouvelle')
        }}
      />
    ) : (
      <UpsellModuleCosmetique
        titre="⚗️ Lots cosmétiques"
        description="Les lots de fabrication cosmétiques font partie du module « Cosmétique ». Il n'est pas encore activé sur ton compte."
        onSabonner={() => naviguer('reglages')}
        onRetour={() => naviguer('accueil')}
      />
    )
  else if (page === 'fiche-cosmetique-impression' && ficheCosmetiqueId)
    contenu = aCosmetique ? (
      <FicheCosmetiqueImpression
        id={ficheCosmetiqueId}
        onRetour={() => setPage('fiche-cosmetique-detail')}
      />
    ) : (
      <UpsellModuleCosmetique
        titre="⚗️ Lots cosmétiques"
        description="Les lots de fabrication cosmétiques font partie du module « Cosmétique ». Il n'est pas encore activé sur ton compte."
        onSabonner={() => naviguer('reglages')}
        onRetour={() => naviguer('accueil')}
      />
    )
  else
    contenu = (
      <Accueil
        onOuvrirCalculateur={() => naviguer('calculateur')}
        onOuvrirIngredients={() => naviguer('ingredients')}
        onOuvrirFormules={() => naviguer('formules')}
        onOuvrirArticles={() => naviguer('articles')}
        onOuvrirStock={() => naviguer('stock')}
        onOuvrirClients={() => naviguer('clients')}
        onOuvrirDevis={() => naviguer('devis')}
        onOuvrirFactures={() => naviguer('factures')}
        onOuvrirDepots={() => naviguer('depots')}
        onOuvrirComptabilite={() => naviguer('comptabilite')}
        onOuvrirFiches={() => naviguer('fiches')}
        onOuvrirReglages={() => naviguer('reglages')}
        onOuvrirFormulateur={() => naviguer('formulateur')}
        onOuvrirFichesCosmetique={() => naviguer('fiches-cosmetique')}
      />
    )

  return (
    <NavProvider
      value={{ sectionCourante: sectionDeLaPage(page), naviguer, definirGardeNavigation }}
    >
      <BandeauRetourAbonnement />
      <Suspense fallback={<EcranChargement />}>{contenu}</Suspense>
    </NavProvider>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      {/* Bannière de mise à jour EN HAUT, au-dessus de tout (y compris de l'écran de connexion) :
          elle doit rester visible quel que soit l'écran affiché. Masquée à l'impression. */}
      <BanniereMaj />
      <AuthProvider>
        <Shell />
      </AuthProvider>
    </ErrorBoundary>
  )
}
